import type { UUID } from '@/types/Id'
import type { Pursuit, PursuitType } from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'
import type { Tag } from '@/types/Tag'
import type { User } from '@/types/User'

export interface RecentlyViewedDocument {
  id: UUID
  name: string
  created_at: string // ISO 8601 datetime
  last_modified: string // ISO 8601 datetime
  last_viewed_at: string // ISO 8601 datetime
  pursuit_id: UUID
  pursuit_title: string
  created_by_user_id: UUID | null
}

export interface RecentlyViewedOutline {
  id: UUID
  title: string | null
  created_at: string // ISO 8601 datetime
  last_modified: string // ISO 8601 datetime
  last_viewed_at: string // ISO 8601 datetime
  pursuit_id: UUID
  pursuit_title: string
  created_by_user_id: UUID | null
}

export interface RecentlyViewedPursuit {
  id: UUID
  type: PursuitType
  organization_id: UUID
  title: string
  created_at: string // ISO 8601 datetime
  last_modified: string // ISO 8601 datetime
  last_viewed_at: string // ISO 8601 datetime
  due_date: string // ISO 8601 datetime
  has_proposal_content: boolean
  stage: PursuitStage
  tag_ids: UUID[]
  user_ids: UUID[]
  description: string | null
}

interface RecentlyViewedDocumentsQueryKeyArgs {
  orgId: UUID | ''
}

interface RecentlyViewedOutlinesQueryKeyArgs {
  orgId: UUID | ''
}

interface RecentlyViewedPursuitsQueryKeyArgs {
  orgId: UUID | ''
}

export const recentlyViewedDocumentsQueryKeys = {
  all: () => ['recently-viewed-documents'] as const,
  organization: (keyArgs: RecentlyViewedDocumentsQueryKeyArgs) =>
    ['recently-viewed-documents', keyArgs.orgId] as const,
}

export const recentlyViewedOutlinesQueryKeys = {
  all: () => ['recently-viewed-outlines'] as const,
  organization: (keyArgs: RecentlyViewedOutlinesQueryKeyArgs) =>
    ['recently-viewed-outlines', keyArgs.orgId] as const,
}

export const recentlyViewedPursuitsQueryKeys = {
  all: () => ['recently-viewed-pursuits'] as const,
  organization: (keyArgs: RecentlyViewedPursuitsQueryKeyArgs) =>
    ['recently-viewed-pursuits', keyArgs.orgId] as const,
}

export const toRecentlyViewedPursuit = (
  pursuit: Pursuit,
): RecentlyViewedPursuit => {
  return {
    id: pursuit.id,
    type: pursuit.type,
    organization_id: pursuit.organization_id,
    title: pursuit.title,
    created_at: pursuit.created_at.toISOString(),
    last_modified: pursuit.last_modified.toISOString(),
    last_viewed_at: new Date().toISOString(),
    due_date: pursuit.due_date.toISOString(),
    has_proposal_content: false,
    stage: pursuit.stage,
    tag_ids: pursuit.tag_ids ?? [],
    user_ids: pursuit.user_ids,
    description: pursuit.description,
  }
}
